import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router';
import { useShallow } from 'zustand/react/shallow';
import { Check, Copy, Download, ExternalLink, Folder, Hash, Images, Lock, Megaphone, MessagesSquare, Radio, Search, Users, Volume2 } from 'lucide-react';
import type { Channel, Invite, ReportCategory } from '@spawnwire/shared';
import { Avatar, Button, clsx, Field, FullscreenDialog, Input, Kbd, Modal, Select, Switch, TextArea } from '../../components/ui';
import { errorMessage, http } from '../../lib/api';
import { loadMembers, openDm } from '../../lib/data';
import { copyText } from '../../lib/hooks';
import { channelPath } from '../../lib/nav';
import { publicOrigin } from '../../lib/platform';
import { actions, displayNameIn, privateChannelName, useStore } from '../../store/store';
import { ui, useUi, type DialogState } from '../../store/ui';
import { EventsDialog } from '../events/EventsDialog';
import { ChannelSettings } from '../settings/ChannelSettings';
import { SpaceSettings } from '../settings/SpaceSettings';
import { UserSettings } from '../settings/UserSettings';
import { SHORTCUTS } from '../shell/shortcuts';
import { SpaceIcon } from '../shell/SpaceIcon';
import { ProfileDialog } from '../members/Profile';
import { CreateSpaceDialog } from '../discover/CreateSpaceDialog';

function useSubmit<T>(fn: () => Promise<T>, onDone?: (r: T) => void) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submit = async (e?: FormEvent) => {
    e?.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const r = await fn();
      onDone?.(r);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };
  return { busy, error, submit };
}

function ErrorText({ error }: { error: string | null }) {
  return error ? (
    <p role="alert" className="mt-3 rounded-md bg-danger-soft px-3 py-2 text-sm text-danger">
      {error}
    </p>
  ) : null;
}

function JoinSpaceDialog() {
  const navigate = useNavigate();
  const [value, setValue] = useState('');
  const code = value.trim().split('/').filter(Boolean).pop() ?? '';
  const { busy, error, submit } = useSubmit(
    () => http.post<{ spaceId: string; channelId: string }>(`/invites/${encodeURIComponent(code)}`),
    (res) => {
      ui.close();
      navigate(`/app/${res.spaceId}/${res.channelId}`);
    },
  );
  return (
    <Modal
      open
      onClose={ui.close}
      title="Join a space"
      description="Paste an invite link or code someone shared with you."
      footer={
        <>
          <Button variant="ghost" onClick={ui.close}>
            Cancel
          </Button>
          <Button loading={busy} disabled={!code} onClick={() => void submit()}>
            Join
          </Button>
        </>
      }
    >
      <form onSubmit={submit}>
        <Field label="Invite link or code" htmlFor="invite" hint={`For example ${publicOrigin()}/invite/hT4kz9QmPa`}>
          <Input id="invite" value={value} onChange={(e) => setValue(e.target.value)} autoFocus />
        </Field>
        <ErrorText error={error} />
      </form>
    </Modal>
  );
}

type CreatableType = 'text' | 'voice' | 'announcement' | 'forum' | 'media' | 'stage' | 'category';

const CHANNEL_TYPES: { value: CreatableType; icon: typeof Hash; label: string; description: string }[] = [
  { value: 'text', icon: Hash, label: 'Text', description: 'Messages, images, links and threads' },
  { value: 'voice', icon: Volume2, label: 'Voice', description: 'Talk, video and screen share' },
  { value: 'announcement', icon: Megaphone, label: 'Announcement', description: 'Moderators post and publish news' },
  { value: 'forum', icon: MessagesSquare, label: 'Forum', description: 'Posts with titles, tags and replies' },
  { value: 'media', icon: Images, label: 'Media', description: 'A gallery of image and video posts' },
  { value: 'stage', icon: Radio, label: 'Stage', description: 'Speakers talk, an audience listens' },
  { value: 'category', icon: Folder, label: 'Category', description: 'Group channels together' },
];

function CreateChannelDialog({ d }: { d: Extract<DialogState, { kind: 'createChannel' }> }) {
  const navigate = useNavigate();
  const [type, setType] = useState<CreatableType>(d.type ?? 'text');
  const [name, setName] = useState('');
  const [priv, setPriv] = useState(false);
  const [roleIds, setRoleIds] = useState<string[]>([]);
  const [userIds, setUserIds] = useState<string[]>([]);
  const [memberQuery, setMemberQuery] = useState('');
  // useShallow keeps these lists stable between renders (a new array each time would re-render forever).
  const categories = useStore(useShallow((s) => Object.values(s.channels).filter((c) => c.spaceId === d.spaceId && c.type === 'category')));
  const roles = useStore(useShallow((s) => (s.spaces[d.spaceId]?.roles ?? []).filter((r) => !r.isDefault).sort((a, b) => b.position - a.position)));
  const members = useStore((s) => s.members[d.spaceId]);
  const meId = useStore((s) => s.me?.id);
  const [parentId, setParentId] = useState<string>(d.parentId ?? '');

  useEffect(() => {
    if (priv) void loadMembers(d.spaceId).catch(() => {});
  }, [priv, d.spaceId]);

  const { busy, error, submit } = useSubmit(
    () =>
      http.post<Channel>(`/spaces/${d.spaceId}/channels`, {
        name,
        type,
        parentId: type === 'category' ? null : parentId || null,
        private: priv,
        ...(priv ? { allowRoleIds: roleIds, allowUserIds: userIds } : {}),
      }),
    (c) => {
      ui.close();
      if (c.type !== 'category') navigate(`/app/${d.spaceId}/${c.id}`);
    },
  );
  const toggle = (list: string[], set: (v: string[]) => void, id: string) => set(list.includes(id) ? list.filter((x) => x !== id) : [...list, id]);
  const slug = type === 'text' || type === 'announcement' || type === 'forum' || type === 'media';
  const pickable = Object.values(members ?? {})
    .filter((m) => m.user.id !== meId && (!memberQuery || m.user.username.includes(memberQuery.toLowerCase()) || m.user.displayName.toLowerCase().includes(memberQuery.toLowerCase())))
    .sort((a, b) => a.user.displayName.localeCompare(b.user.displayName))
    .slice(0, 40);

  return (
    <Modal
      open
      onClose={ui.close}
      size="lg"
      title={type === 'category' ? 'Create category' : 'Create channel'}
      footer={
        <>
          <Button variant="ghost" onClick={ui.close}>
            Cancel
          </Button>
          <Button loading={busy} disabled={!name.trim()} onClick={() => void submit()}>
            Create
          </Button>
        </>
      }
    >
      <form onSubmit={submit} className="space-y-4">
        <fieldset>
          <legend className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-muted">Type</legend>
          <div className="grid gap-2 sm:grid-cols-2">
            {CHANNEL_TYPES.map((t) => (
              <label key={t.value} className={clsx('flex cursor-pointer items-center gap-3 rounded-lg border px-3 py-2.5', type === t.value ? 'border-accent bg-accent-soft' : 'border-line hover:bg-hover')}>
                <input type="radio" name="type" value={t.value} checked={type === t.value} onChange={() => setType(t.value)} className="sr-only" />
                <t.icon className="size-5 shrink-0 text-muted" aria-hidden />
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-medium text-fg">{t.label}</span>
                  <span className="block text-xs text-muted">{t.description}</span>
                </span>
                {type === t.value && <Check className="size-4 shrink-0 text-accent" aria-hidden />}
              </label>
            ))}
          </div>
        </fieldset>
        <Field label="Name" htmlFor="channel-name" hint={slug ? 'Lowercase, with dashes instead of spaces.' : undefined}>
          <Input
            id="channel-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={type === 'category' ? 'New category' : type === 'voice' ? 'Hangout' : type === 'stage' ? 'Town hall' : type === 'forum' ? 'help-and-questions' : type === 'media' ? 'screenshots' : 'new-channel'}
            maxLength={100}
            autoFocus
          />
        </Field>
        {type !== 'category' && categories.length > 0 && (
          <Field label="Category" htmlFor="channel-cat" hint="It starts with the category's permissions.">
            <Select id="channel-cat" value={parentId} onChange={setParentId} options={[{ value: '', label: 'No category' }, ...categories.map((c) => ({ value: c.id, label: c.name }))]} />
          </Field>
        )}
        <Switch
          checked={priv}
          onChange={setPriv}
          label={type === 'category' ? 'Private category' : 'Private channel'}
          description="Hidden from everyone except you, the roles and members you pick, and people with Administrator. You can change who can see it later in its settings."
        />
        {priv && (
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-muted">Roles who can see it</p>
              {roles.length === 0 ? (
                <p className="text-sm text-faint">This space has no roles yet.</p>
              ) : (
                <ul className="scroll-thin max-h-48 space-y-0.5 overflow-y-auto rounded-lg border border-line p-1">
                  {roles.map((r) => (
                    <li key={r.id}>
                      <label className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm text-fg hover:bg-hover">
                        <input type="checkbox" checked={roleIds.includes(r.id)} onChange={() => toggle(roleIds, setRoleIds, r.id)} className="size-4 accent-[var(--p-accent)]" />
                        <span className="size-2.5 rounded-full" style={{ background: r.color ?? 'var(--p-faint)' }} aria-hidden />
                        <span className="truncate">{r.name}</span>
                      </label>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div>
              <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-muted">Members who can see it</p>
              <Input value={memberQuery} onChange={(e) => setMemberQuery(e.target.value)} placeholder="Search members" aria-label="Search members" className="mb-1 h-8" />
              <ul className="scroll-thin max-h-40 space-y-0.5 overflow-y-auto rounded-lg border border-line p-1">
                {pickable.map((m) => (
                  <li key={m.user.id}>
                    <label className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1 text-sm text-fg hover:bg-hover">
                      <input type="checkbox" checked={userIds.includes(m.user.id)} onChange={() => toggle(userIds, setUserIds, m.user.id)} className="size-4 accent-[var(--p-accent)]" />
                      <Avatar user={m.user} size={20} />
                      <span className="truncate">{displayNameIn(useStore.getState(), m.user.id, d.spaceId)}</span>
                    </label>
                  </li>
                ))}
                {pickable.length === 0 && <li className="px-2 py-1 text-sm text-faint">No one found.</li>}
              </ul>
            </div>
          </div>
        )}
        <ErrorText error={error} />
      </form>
    </Modal>
  );
}

function InviteDialog({ d }: { d: Extract<DialogState, { kind: 'invite' }> }) {
  const space = useStore((s) => s.spaces[d.spaceId]);
  const channel = useStore((s) => s.channels[d.channelId]);
  const [maxAge, setMaxAge] = useState('604800');
  const [maxUses, setMaxUses] = useState('0');
  const [invite, setInvite] = useState<Invite | null>(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    http
      .post<Invite>(`/channels/${d.channelId}/invites`, { maxAgeSeconds: Number(maxAge), maxUses: Number(maxUses) })
      .then((inv) => !cancelled && setInvite(inv))
      .catch((err) => !cancelled && setError(errorMessage(err)));
    return () => {
      cancelled = true;
    };
  }, [d.channelId, maxAge, maxUses]);

  const link = invite ? `${publicOrigin()}/invite/${invite.code}` : '';
  return (
    <Modal open onClose={ui.close} title={`Invite people to ${space?.name ?? 'this space'}`} description={channel ? `They will land in #${channel.name}.` : undefined}>
      <div className="space-y-4">
        <div className="flex gap-2">
          <Input readOnly value={link || 'Creating link…'} aria-label="Invite link" onFocus={(e) => e.currentTarget.select()} />
          <Button
            icon={copied ? Check : Copy}
            disabled={!link}
            onClick={() =>
              void copyText(link).then(() => {
                setCopied(true);
                setTimeout(() => setCopied(false), 1500);
              })
            }
          >
            {copied ? 'Copied' : 'Copy'}
          </Button>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Expire after" htmlFor="inv-age">
            <Select id="inv-age" value={maxAge} onChange={setMaxAge} options={[{ value: '1800', label: '30 minutes' }, { value: '3600', label: '1 hour' }, { value: '86400', label: '1 day' }, { value: '604800', label: '7 days' }, { value: '0', label: 'Never' }]} />
          </Field>
          <Field label="Max uses" htmlFor="inv-uses">
            <Select id="inv-uses" value={maxUses} onChange={setMaxUses} options={[{ value: '0', label: 'No limit' }, { value: '1', label: '1 use' }, { value: '5', label: '5 uses' }, { value: '10', label: '10 uses' }, { value: '25', label: '25 uses' }, { value: '100', label: '100 uses' }]} />
          </Field>
        </div>
        <p className="text-xs text-faint">Changing these creates a new link. Old links keep working until they expire or are revoked in space settings.</p>
        <ErrorText error={error} />
      </div>
    </Modal>
  );
}

function ConfirmDialog({ d }: { d: Extract<DialogState, { kind: 'confirm' }> }) {
  const { busy, error, submit } = useSubmit(async () => d.onConfirm(), () => ui.close());
  return (
    <Modal
      open
      onClose={ui.close}
      title={d.title}
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={ui.close}>
            Cancel
          </Button>
          <Button variant={d.danger ? 'danger' : 'primary'} loading={busy} onClick={() => void submit()} autoFocus>
            {d.confirmLabel}
          </Button>
        </>
      }
    >
      <p className="text-sm text-muted">{d.body}</p>
      <ErrorText error={error} />
    </Modal>
  );
}

const CATEGORIES: { value: ReportCategory; label: string }[] = [
  { value: 'spam', label: 'Spam or scam' },
  { value: 'harassment', label: 'Harassment or bullying' },
  { value: 'hate', label: 'Hateful content' },
  { value: 'violence', label: 'Threats or violence' },
  { value: 'self_harm', label: 'Self-harm' },
  { value: 'sexual', label: 'Sexual content' },
  { value: 'other', label: 'Something else' },
];

function ReportDialog({ d }: { d: Extract<DialogState, { kind: 'report' }> }) {
  const [category, setCategory] = useState<ReportCategory>('spam');
  const [reason, setReason] = useState('');
  const { busy, error, submit } = useSubmit(
    () => http.post('/reports', { category, reason, messageId: d.messageId, userId: d.messageId ? undefined : d.userId, spaceId: d.spaceId ?? undefined }),
    () => {
      ui.close();
      ui.toast('Thanks. Your report was sent to the moderators.', 'success');
    },
  );
  return (
    <Modal
      open
      onClose={ui.close}
      title={d.messageId ? 'Report message' : 'Report user'}
      description={d.spaceId ? "Reports go to this space's moderators." : 'Reports about direct messages are stored for the server operators.'}
      footer={
        <>
          <Button variant="ghost" onClick={ui.close}>
            Cancel
          </Button>
          <Button variant="danger" loading={busy} onClick={() => void submit()}>
            Send report
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Field label="What is the problem?" htmlFor="rep-cat">
          <Select id="rep-cat" value={category} onChange={setCategory} options={CATEGORIES} />
        </Field>
        <Field label="Details (optional)" htmlFor="rep-reason">
          <TextArea id="rep-reason" rows={3} value={reason} onChange={(e) => setReason(e.target.value)} maxLength={1000} />
        </Field>
        <ErrorText error={error} />
      </div>
    </Modal>
  );
}

function ModerateDialog({ d }: { d: Extract<DialogState, { kind: 'moderate' }> }) {
  const name = useStore((s) => displayNameIn(s, d.userId, d.spaceId));
  const [reason, setReason] = useState('');
  const [duration, setDuration] = useState('3600');
  const [deleteHours, setDeleteHours] = useState('0');
  const { busy, error, submit } = useSubmit(
    () => {
      if (d.action === 'kick') return http.del(`/spaces/${d.spaceId}/members/${d.userId}`, { reason });
      if (d.action === 'ban') return http.put(`/spaces/${d.spaceId}/bans/${d.userId}`, { reason, deleteMessageHours: Number(deleteHours) });
      return http.patch(`/spaces/${d.spaceId}/members/${d.userId}`, { timeoutUntil: new Date(Date.now() + Number(duration) * 1000).toISOString(), reason });
    },
    () => {
      ui.close();
      ui.toast(d.action === 'kick' ? `${name} was kicked.` : d.action === 'ban' ? `${name} was banned.` : `${name} was timed out.`, 'success');
    },
  );
  const title = d.action === 'kick' ? `Kick ${name}` : d.action === 'ban' ? `Ban ${name}` : `Time out ${name}`;
  return (
    <Modal
      open
      onClose={ui.close}
      title={title}
      description={
        d.action === 'kick' ? 'They can rejoin with a new invite.' : d.action === 'ban' ? 'They are removed and cannot rejoin until unbanned.' : 'They can read but not send messages, react, or join voice until the timeout ends.'
      }
      footer={
        <>
          <Button variant="ghost" onClick={ui.close}>
            Cancel
          </Button>
          <Button variant="danger" loading={busy} onClick={() => void submit()}>
            {d.action === 'kick' ? 'Kick' : d.action === 'ban' ? 'Ban' : 'Time out'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {d.action === 'timeout' && (
          <Field label="Duration" htmlFor="mod-dur">
            <Select id="mod-dur" value={duration} onChange={setDuration} options={[{ value: '60', label: '60 seconds' }, { value: '300', label: '5 minutes' }, { value: '600', label: '10 minutes' }, { value: '3600', label: '1 hour' }, { value: '86400', label: '1 day' }, { value: '604800', label: '1 week' }]} />
          </Field>
        )}
        {d.action === 'ban' && (
          <Field label="Delete their recent messages" htmlFor="mod-del">
            <Select id="mod-del" value={deleteHours} onChange={setDeleteHours} options={[{ value: '0', label: "Don't delete any" }, { value: '1', label: 'Previous hour' }, { value: '24', label: 'Previous 24 hours' }, { value: '168', label: 'Previous 7 days' }]} />
          </Field>
        )}
        <Field label="Reason (shown in the audit log)" htmlFor="mod-reason">
          <Input id="mod-reason" value={reason} onChange={(e) => setReason(e.target.value)} maxLength={512} />
        </Field>
        <ErrorText error={error} />
      </div>
    </Modal>
  );
}

function NewGroupDialog() {
  const navigate = useNavigate();
  const friends = useStore(useShallow((s) => Object.values(s.relationships).filter((r) => r.type === 'friend')));
  const [picked, setPicked] = useState<string[]>([]);
  const [filter, setFilter] = useState('');
  const { busy, error, submit } = useSubmit(
    async () => (picked.length === 1 ? openDm(picked[0]!) : http.post<Channel>('/users/@me/channels', { recipientIds: picked })),
    (c) => {
      if (!c) return;
      actions.upsertChannel(c);
      ui.close();
      navigate(`/app/@me/${c.id}`);
    },
  );
  const shown = friends.filter((f) => !filter || f.user.displayName.toLowerCase().includes(filter.toLowerCase()) || f.user.username.includes(filter.toLowerCase()));
  return (
    <Modal
      open
      onClose={ui.close}
      title="Start a conversation"
      description="Pick one friend for a direct message, or up to nine for a group."
      footer={
        <>
          <Button variant="ghost" onClick={ui.close}>
            Cancel
          </Button>
          <Button loading={busy} disabled={picked.length === 0} onClick={() => void submit()}>
            {picked.length > 1 ? 'Create group' : 'Open conversation'}
          </Button>
        </>
      }
    >
      <Input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Search friends" aria-label="Search friends" className="mb-3" />
      {friends.length === 0 ? (
        <p className="text-sm text-muted">Add some friends first. Group conversations can only include your friends.</p>
      ) : (
        <ul className="max-h-72 space-y-1 overflow-y-auto">
          {shown.map((f) => {
            const on = picked.includes(f.userId);
            return (
              <li key={f.userId}>
                <label className={clsx('flex cursor-pointer items-center gap-3 rounded-md px-2 py-1.5', on ? 'bg-accent-soft' : 'hover:bg-hover')}>
                  <input
                    type="checkbox"
                    checked={on}
                    disabled={!on && picked.length >= 9}
                    onChange={() => setPicked(on ? picked.filter((p) => p !== f.userId) : [...picked, f.userId])}
                    className="accent-[var(--p-accent)]"
                  />
                  <Avatar user={f.user} size={28} />
                  <span className="text-sm text-fg">{f.user.displayName}</span>
                  <span className="text-xs text-faint">@{f.user.username}</span>
                </label>
              </li>
            );
          })}
        </ul>
      )}
      <ErrorText error={error} />
    </Modal>
  );
}

function CreateThreadDialog({ d }: { d: Extract<DialogState, { kind: 'createThread' }> }) {
  const [name, setName] = useState(d.defaultName);
  const { busy, error, submit } = useSubmit(
    () => http.post<Channel>(`/channels/${d.channelId}/messages/${d.messageId}/threads`, { name }),
    (thread) => {
      actions.upsertChannel(thread);
      ui.close();
      ui.openThread(thread.id);
    },
  );
  return (
    <Modal
      open
      onClose={ui.close}
      title="Start a thread"
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={ui.close}>
            Cancel
          </Button>
          <Button loading={busy} disabled={!name.trim()} onClick={() => void submit()}>
            Create thread
          </Button>
        </>
      }
    >
      <form onSubmit={submit}>
        <Field label="Thread name" htmlFor="thread-name">
          <Input id="thread-name" value={name} onChange={(e) => setName(e.target.value)} maxLength={100} autoFocus />
        </Field>
        <ErrorText error={error} />
      </form>
    </Modal>
  );
}

interface SwitchItem {
  key: string;
  label: string;
  detail: string;
  to: string;
  icon: 'channel' | 'voice' | 'dm' | 'space' | 'friend' | 'private';
  avatar?: { id: string; displayName: string; avatarUrl: string | null };
  unread?: boolean;
}

function QuickSwitcher() {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [sel, setSel] = useState(0);
  const items = useMemo(() => {
    const state = useStore.getState();
    const out: SwitchItem[] = [];
    for (const c of Object.values(state.channels)) {
      if (c.type === 'category' || c.type === 'thread') continue;
      if (c.spaceId) {
        out.push({ key: c.id, label: c.name, detail: state.spaces[c.spaceId]?.name ?? '', to: channelPath(c), icon: c.type === 'voice' || c.type === 'stage' ? 'voice' : 'channel' });
      } else {
        const partner = c.recipients?.find((r) => r.id !== state.me?.id);
        out.push({ key: c.id, label: privateChannelName(state, c), detail: c.type === 'group_dm' ? 'Group' : 'Direct message', to: channelPath(c), icon: 'dm', avatar: c.type === 'dm' ? partner : undefined });
      }
    }
    for (const s of Object.values(state.spaces)) out.push({ key: s.id, label: s.name, detail: 'Space', to: `/app/${s.id}`, icon: 'space' });
    for (const r of Object.values(state.relationships)) {
      if (r.type !== 'friend') continue;
      const hasDm = Object.values(state.channels).some((c) => c.type === 'dm' && c.recipients?.some((x) => x.id === r.userId));
      if (!hasDm) out.push({ key: `f${r.userId}`, label: r.user.displayName, detail: `@${r.user.username}`, to: `friend:${r.userId}`, icon: 'friend', avatar: r.user });
    }
    return out;
  }, []);
  const filtered = useMemo(() => {
    const s = q.toLowerCase().replace(/^[#@*]/, '');
    const list = s ? items.filter((i) => i.label.toLowerCase().includes(s) || i.detail.toLowerCase().includes(s)) : items;
    return list.sort((a, b) => Number(b.label.toLowerCase().startsWith(s)) - Number(a.label.toLowerCase().startsWith(s))).slice(0, 12);
  }, [q, items]);

  const go = async (item: SwitchItem) => {
    ui.close();
    if (item.to.startsWith('friend:')) {
      const c = await openDm(item.to.slice(7));
      if (c) navigate(`/app/@me/${c.id}`);
    } else navigate(item.to);
  };

  return (
    <Modal open onClose={ui.close} title="Jump to" size="md">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-faint" aria-hidden />
        <Input
          autoFocus
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setSel(0);
          }}
          placeholder="Where would you like to go?"
          aria-label="Search channels, conversations and spaces"
          className="h-12 pl-9 text-base"
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown') {
              e.preventDefault();
              setSel((i) => Math.min(filtered.length - 1, i + 1));
            } else if (e.key === 'ArrowUp') {
              e.preventDefault();
              setSel((i) => Math.max(0, i - 1));
            } else if (e.key === 'Enter' && filtered[sel]) {
              e.preventDefault();
              void go(filtered[sel]);
            }
          }}
        />
      </div>
      <ul className="mt-3 space-y-0.5" role="listbox" aria-label="Results">
        {filtered.map((item, i) => {
          const Icon = item.icon === 'voice' ? Volume2 : item.icon === 'space' ? Users : item.icon === 'private' ? Lock : Hash;
          return (
            <li key={item.key} role="option" aria-selected={i === sel}>
              <button type="button" onMouseEnter={() => setSel(i)} onClick={() => void go(item)} className={clsx('flex w-full items-center gap-3 rounded-md px-3 py-2 text-left', i === sel ? 'bg-active' : '')}>
                {item.avatar ? <Avatar user={item.avatar} size={22} /> : item.icon === 'space' ? <SpaceIcon name={item.label} iconUrl={useStore.getState().spaces[item.key]?.iconUrl ?? null} size={22} /> : <Icon className="size-5 text-faint" aria-hidden />}
                <span className="truncate font-medium text-fg">{item.label}</span>
                <span className="ml-auto truncate text-xs text-faint">{item.detail}</span>
              </button>
            </li>
          );
        })}
        {filtered.length === 0 && <li className="px-3 py-6 text-center text-sm text-muted">Nothing matches.</li>}
      </ul>
      <p className="mt-3 text-xs text-faint">
        <Kbd>{'↑'}</Kbd> <Kbd>{'↓'}</Kbd> to choose, <Kbd>Enter</Kbd> to go, <Kbd>Esc</Kbd> to close
      </p>
    </Modal>
  );
}

function ShortcutsDialog() {
  return (
    <Modal open onClose={ui.close} title="Keyboard shortcuts" size="lg">
      <dl className="divide-y divide-line">
        {SHORTCUTS.map((s) => (
          <div key={s.keys} className="flex items-center justify-between gap-4 py-2.5">
            <dt className="text-sm text-fg">{s.action}</dt>
            <dd className="shrink-0">
              <Kbd>{s.keys}</Kbd>
            </dd>
          </div>
        ))}
      </dl>
      <p className="mt-3 text-xs text-faint">On macOS, use Cmd in place of Ctrl.</p>
    </Modal>
  );
}

function Lightbox({ d }: { d: Extract<DialogState, { kind: 'lightbox' }> }) {
  return (
    <FullscreenDialog open onClose={ui.close} title={d.filename}>
      <div className="flex size-full flex-col bg-black/90" onClick={ui.close}>
        <div className="flex items-center justify-between gap-3 p-3 text-white" onClick={(e) => e.stopPropagation()}>
          <span className="truncate text-sm">{d.filename}</span>
          <div className="flex gap-2">
            <a href={d.url} target="_blank" rel="noopener noreferrer" className="rounded-md p-2 hover:bg-white/10" aria-label="Open original">
              <ExternalLink className="size-5" />
            </a>
            <a href={`${d.url}?download=1`} download={d.filename} className="rounded-md p-2 hover:bg-white/10" aria-label="Download">
              <Download className="size-5" />
            </a>
            <Button variant="secondary" size="sm" onClick={ui.close}>
              Close
            </Button>
          </div>
        </div>
        <div className="flex min-h-0 flex-1 items-center justify-center p-4">
          <img src={d.url} alt={d.filename} className="max-h-full max-w-full object-contain" onClick={(e) => e.stopPropagation()} />
        </div>
      </div>
    </FullscreenDialog>
  );
}

export function Dialogs() {
  const d = useUi((s) => s.dialog);
  if (!d) return null;
  switch (d.kind) {
    case 'createSpace':
      return <CreateSpaceDialog />;
    case 'joinSpace':
      return <JoinSpaceDialog />;
    case 'createChannel':
      return <CreateChannelDialog d={d} />;
    case 'invite':
      return <InviteDialog d={d} />;
    case 'confirm':
      return <ConfirmDialog d={d} />;
    case 'report':
      return <ReportDialog d={d} />;
    case 'moderate':
      return <ModerateDialog d={d} />;
    case 'newGroup':
      return <NewGroupDialog />;
    case 'createThread':
      return <CreateThreadDialog d={d} />;
    case 'quickSwitcher':
      return <QuickSwitcher />;
    case 'shortcuts':
      return <ShortcutsDialog />;
    case 'lightbox':
      return <Lightbox d={d} />;
    case 'profile':
      return <ProfileDialog key={d.userId} userId={d.userId} spaceId={d.spaceId} anchor={d.anchor} />;
    case 'userSettings':
      return <UserSettings section={d.section} />;
    case 'spaceSettings':
      return <SpaceSettings spaceId={d.spaceId} section={d.section} />;
    case 'channelSettings':
      return <ChannelSettings channelId={d.channelId} />;
    case 'events':
      return <EventsDialog spaceId={d.spaceId} />;
    default:
      return null;
  }
}
